import { blockKey } from '@hyphengroup/cms-components/calculatedAttributes/blockKey';
import type { LoanFields } from '@hyphengroup/cms-components/calculatedAttributes/collect';
import type { CmsBaseComponent } from '../cmsService/types';
import { rankLoanFields } from './rules';
import type {
  LoanFieldsRank,
  LoanScopeInput,
  LoanScopePlan,
  LoanTerms,
  ModuleLoanPlan,
} from './types';
import type { ComponentOfType } from '$/components/cms/helpers/componentTree';
import { getLoanSearchParamsWithDefaultComponentParam } from '$/components/cms/helpers/loanSearchParams';

/**
 * The module whose loan settings the page scope follows: the first Comparison
 * Table, else the first Selected Products carrying any loan setting — what the
 * page filter has always been configured from.
 *
 * "First on the page" is today's behaviour, kept on purpose; "nearest above the
 * asking module" is an open question in CMS-1890 and would change this
 * function alone — which is why the list is scanned as given rather than
 * through the tree helpers: both callers hand in `flattenComponents` output,
 * and those helpers flatten again, so every Prefilled Block child was visited
 * twice. Harmless for `.find`, a trap the moment position starts to matter.
 */
const findCarrier = (
  components: CmsBaseComponent[] | undefined,
): LoanFields | undefined => {
  const table = components?.find(
    (
      component,
    ): component is ComponentOfType<'ComponentPagesComponentComparisonTable'> =>
      component.__typename === 'ComponentPagesComponentComparisonTable',
  );
  if (table?.default_loan_amount || table?.default_loan_tenure) {
    return {
      amount: table.default_loan_amount,
      tenure: table.default_loan_tenure,
    };
  }

  const selected = (components ?? [])
    .filter(
      (
        component,
      ): component is ComponentOfType<'ComponentPagesComponentSelectedProducts'> =>
        component.__typename === 'ComponentPagesComponentSelectedProducts',
    )
    .find(
      (component) =>
        component.default_loan_amount || component.default_loan_tenure,
    );

  return selected
    ? {
        amount: selected.default_loan_amount,
        tenure: selected.default_loan_tenure,
      }
    : undefined;
};

/**
 * The loan-scope engine (CMS-1890): which terms every PL module on the page
 * calculates with, and which scope it belongs to. Resolved HERE and nowhere
 * else — the server pass and the client refetch call this same function.
 *
 * Every parameter walks the chain on its own:
 *
 * ```
 * own (statement) ?? url ?? own (default) ?? carrier ?? page ?? market ?? system
 * ```
 *
 * A module stating an amount and no tenure has stated an amount; its tenure
 * still comes from the carrier or the page. The market level does not exist
 * yet — it is a Strapi entry per market, and its own ticket.
 * `getLoanSearchParamsWithDefaultComponentParam` fills the system defaults, as
 * it does for every other caller.
 */
export const planLoanScopes = (input: LoanScopeInput): LoanScopePlan => {
  const carrier = findCarrier(input.components);
  const page: LoanFields = {
    amount: input.page?.default_loan_amount,
    tenure: input.page?.default_loan_tenure,
  };

  const resolve = (
    own: LoanFields | undefined,
    rank: LoanFieldsRank,
  ): LoanTerms => {
    const level = (field: keyof LoanFields): string | number | undefined =>
      (rank === 'statement' ? own?.[field] : undefined) ??
      input.urlParams?.[field] ??
      (rank === 'default' ? own?.[field] : undefined) ??
      carrier?.[field] ??
      page[field];

    // The URL is already applied above, per field; the helper fills the
    // market/system levels.
    const filled = getLoanSearchParamsWithDefaultComponentParam(
      input.brand,
      undefined,
      {
        amount: level('amount')?.toString(),
        tenure: level('tenure')?.toString(),
      },
    );
    return { amount: Number(filled.amount), tenure: Number(filled.tenure) };
  };

  const pageTerms = resolve(undefined, 'default');
  const requestByModule = new Map(
    input.requests
      .filter((request) => request.provider === 'PL_CALCULATOR')
      .map((request) => [request.moduleId, request] as const),
  );

  // Every block gets a plan, not only those that calculate: a block with no
  // request is in the page scope, and an apply CTA inside it still needs the
  // terms.
  const modules: Record<string, ModuleLoanPlan> = {};
  input.components?.forEach((component, position) => {
    const moduleId = blockKey(component);
    const request = requestByModule.get(moduleId);
    if (!request || request.provider !== 'PL_CALCULATOR') {
      modules[moduleId] = { moduleId, terms: pageTerms, scope: 'page' };
      return;
    }
    const rank = rankLoanFields(input.components?.[position], request.criteria);
    modules[moduleId] = {
      moduleId,
      terms: resolve(request.criteria.loanFields, rank),
      scope: rank === 'statement' ? 'own' : 'page',
    };
  });

  return { pageTerms, modules };
};
